import React, { useState, useEffect, useRef, useCallback } from 'react';
import { IonContent, IonPage, IonHeader, IonToolbar, IonTitle, IonButtons, IonButton, IonToast, IonLoading } from '@ionic/react';
import { useHistory } from 'react-router-dom';
import { ChevronLeft, Sparkles, Send, User, Bot, Loader2, Trash2, Clock, AlertTriangle } from 'lucide-react';
import { capacitorDbService } from '../services/capacitorDb.service';
import { useAuthStore } from '../store/authStore';
import { aiGatewayService } from '../services/aiGateway.service';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

const AIChatScreen: React.FC = () => {
  const history = useHistory();
  const { user } = useAuthStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [sessionId, setSessionId] = useState<string>('');
  const [usageCount, setUsageCount] = useState(0);
  const [tier, setTier] = useState('free');

  const getCurrentUserId = useCallback(async (): Promise<string | null> => {
    if (user?.id) return user.id;
    const globalId = (window as any).__NURWALLET_USER_ID__;
    if (globalId) return globalId;
    try {
      await capacitorDbService.init();
      const guestUser = await capacitorDbService.getUserByEmail('guest@nurwallet.local');
      if (guestUser) { (window as any).__NURWALLET_USER_ID__ = guestUser.id; return guestUser.id; }
    } catch (e) {}
    try {
      const allUsers = await capacitorDbService.getAllUsers?.();
      if (allUsers?.length > 0) { (window as any).__NURWALLET_USER_ID__ = allUsers[0].id; return allUsers[0].id; }
    } catch (e) {}
    return null;
  }, [user?.id]);

  const loadChatHistory = useCallback(async () => {
    try {
      const userId = await getCurrentUserId();
      if (!userId) { setIsPageLoading(false); return; }

      // Tier check - keep your existing logic
      setTier('free'); // or however you determine tier

      // Get today's usage
      const todayUsage = await capacitorDbService.getTodayAIUsage(userId).catch(() => 0);
      setUsageCount(todayUsage);

      // Load chat sessions
      const sessions = await capacitorDbService.getChatSessions(userId).catch(() => []);
      if (sessions.length > 0) {
        setSessionId(sessions[0].id);
        const msgs = await capacitorDbService.getChatMessages(userId, sessions[0].id).catch(() => []);
        setMessages(msgs.map((m: any) => ({
          id: m.id || `${Date.now()}-${Math.random()}`,
          role: m.role === 'ASSISTANT' ? 'assistant' : 'user',
          content: m.content,
          timestamp: new Date(m.createdAt || Date.now()),
        })));
      } else {
        // Create new session
        const newSession = await capacitorDbService.createChatSession(userId, 'New Chat').catch(() => null);
        if (newSession) setSessionId(newSession.id);
      }
    } catch (error) {
      console.error('[AIChat] Load error:', error);
    } finally {
      setIsPageLoading(false);
    }
  }, [getCurrentUserId]);

  useEffect(() => { loadChatHistory(); }, [loadChatHistory]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

   const handleSend = async () => {
    if (!inputText.trim() || isLoading) return;

    const userId = await getCurrentUserId();
    if (!userId) {
      showMsg('❌ Please login first');
      return;
    }

    // Check tier limits (keep your existing limit check)
    if (usageCount >= getDailyLimit()) {
      showMsg('❌ Daily limit reached. Upgrade for more messages.');
      return;
    }

    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: 'user',
      content: inputText.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsLoading(true);

    try {
      // Initialize AI Gateway
      await aiGatewayService.init();

      // Save user message to local SQLite (unchanged)
      if (sessionId) {
        await capacitorDbService.createChatMessage({
          userId,
          sessionId,
          role: 'USER',
          content: userMessage.content,
        }).catch(() => {});
      }

      // ==========================================================
      // NEW: Call AI Gateway with RAG (replaces aiService.chat)
      // ==========================================================
      const history = messages.slice(-5).map(m => ({ role: m.role, content: m.content }));
      
      const aiResult = await aiGatewayService.chat(userMessage.content, userId, {
        sessionId: sessionId ? parseInt(sessionId) : undefined,
        useRAG: true,
        history,
      });

      const assistantMessage: ChatMessage = {
        id: `${Date.now()}-assistant`,
        role: 'assistant',
        content: aiResult.content || 'I apologize, I could not generate a response. Please try again.',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Save assistant message to local SQLite (unchanged)
      if (sessionId) {
        await capacitorDbService.createChatMessage({
          userId,
          sessionId,
          role: 'ASSISTANT',
          content: assistantMessage.content,
        }).catch(() => {});
      }

      // Increment usage (unchanged)
      await capacitorDbService.incrementAIUsage(userId).catch(() => {});
      const newUsage = await capacitorDbService.getTodayAIUsage(userId).catch(() => 0);
      setUsageCount(newUsage);

    } catch (error: any) {
      console.error('[AIChat] Error:', error);
      showMsg('❌ Failed to get response. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearChat = async () => {
    const userId = await getCurrentUserId();
    if (!userId) return;

    // Create new session
    const newSession = await capacitorDbService.createChatSession(userId, 'New Chat').catch(() => null);
    if (newSession) {
      setSessionId(newSession.id);
      setMessages([]);
      showMsg('✅ Chat cleared');
    }
  };

  const showMsg = (msg: string) => { setToastMessage(msg); setShowToast(true); };

  const getTierColor = () => {
    switch (tier) {
      case 'premium': return 'bg-amber-500 text-white';
      case 'paid': return 'bg-blue-500 text-white';
      default: return 'bg-gray-200 text-gray-600';
    }
  };

  const getDailyLimit = () => {
    switch (tier) {
      case 'premium': return 100;
      case 'paid': return 50;
      default: return 10;
    }
  };

  if (isPageLoading) {
    return (
      <IonPage>
        <IonContent style={{ '--background': '#f9fafb' } as any}>
          <div className="flex items-center justify-center h-full">
            <Loader2 size={32} className="text-emerald-600 animate-spin" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader className="ion-no-border">
        <IonToolbar style={{ '--background': '#f9fafb' } as any}>
          <IonButtons slot="start">
            <IonButton onClick={() => history.push('/home')} fill="clear" style={{ '--color': '#1f2937' } as any}>
              <ChevronLeft size={24} />
            </IonButton>
          </IonButtons>
          <IonTitle className="text-lg font-bold flex items-center gap-2">
            <Sparkles size={18} className="text-emerald-600" /> AI Companion
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleClearChat} fill="clear" style={{ '--color': '#9ca3af' } as any}>
              <Trash2 size={18} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent style={{ '--background': '#f9fafb' } as any}>
        {/* Tier Badge */}
        <div className="px-5 pt-2 pb-3">
          <div className="flex items-center justify-between">
            <span className={`px-3 py-1 rounded-full text-xs font-bold ${getTierColor()}`}>
              {tier.toUpperCase()}
            </span>
            <span className="text-xs text-gray-500">
              {usageCount} / {getDailyLimit()} messages today
            </span>
          </div>
          {usageCount >= getDailyLimit() * 0.8 && (
            <div className="mt-2 p-2 bg-amber-50 rounded-lg flex items-center gap-2">
              <AlertTriangle size={14} className="text-amber-500" />
              <p className="text-xs text-amber-700">You're near your daily limit. Upgrade for more messages.</p>
            </div>
          )}
        </div>

        {/* Messages */}
        <div className="px-4 pb-20">
          {messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <Sparkles size={28} className="text-emerald-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">NurWallet AI</h3>
              <p className="text-sm text-gray-500 max-w-xs mb-6">
                Your personal Islamic financial advisor. Ask me about zakat, retirement, takaful, investments, and more.
              </p>
              <div className="grid grid-cols-2 gap-2 w-full max-w-sm">
                {[
                  'How much zakat do I owe?',
                  'Is my retirement on track?',
                  'What takaful coverage do I need?',
                  'How to invest in Sukuk?',
                ].map((suggestion) => (
                  <button
                    key={suggestion}
                    onClick={() => { setInputText(suggestion); }}
                    className="p-3 bg-white rounded-xl border border-gray-100 text-xs text-gray-600 text-left hover:bg-emerald-50 transition"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => (
                <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.role === 'user' ? 'bg-emerald-600' : 'bg-gray-200'
                  }`}>
                    {msg.role === 'user' ? <User size={14} className="text-white" /> : <Bot size={14} className="text-gray-600" />}
                  </div>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user' 
                      ? 'bg-emerald-600 text-white rounded-tr-none' 
                      : 'bg-white border border-gray-100 text-gray-800 rounded-tl-none'
                  }`}>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
                    <p className={`text-[10px] mt-1 ${msg.role === 'user' ? 'text-emerald-200' : 'text-gray-400'}`}>
                      {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                    <Bot size={14} className="text-gray-600" />
                  </div>
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-none px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Loader2 size={14} className="animate-spin text-emerald-600" />
                      <span className="text-xs text-gray-500">Thinking...</span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </IonContent>

      {/* Input Area */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 pb-safe z-50">
        <div className="flex items-center gap-2">
          <input
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Ask about your finances..."
            className="flex-1 bg-gray-100 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            disabled={isLoading}
          />
          <button
            onClick={handleSend}
            disabled={!inputText.trim() || isLoading}
            className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center disabled:opacity-40"
          >
            <Send size={18} className="text-white" />
          </button>
        </div>
      </div>

      <IonToast isOpen={showToast} onDidDismiss={() => setShowToast(false)} message={toastMessage} duration={3000} position="top" color={toastMessage.includes('✅') ? 'success' : 'danger'} />
    </IonPage>
  );
};

export default AIChatScreen;
